/**
 * Maps the COB (coordination-of-benefits) block from
 * `coverage_result.processing_metadata.cob` into the read-only
 * {@link AdditionalPayerInformation} display model.
 *
 * @module coverageInfo/helpers/mapAdditionalPayerCoverageInformation
 */
import type {
  AdditionalPayer,
  AdditionalPayerInformation,
} from "../../../data-model/coverageInformation";
import type {
  CobAdditionalPayer,
  CobMetadata,
  CoverageResult,
} from "../../../data-model/medicalPaOrdersModel";
import { formatDateToUTCWithFormat } from "../../../utils/formatDate";

/** Placeholder rendered when an additional-payer field is missing or empty. */
const EMPTY_DISPLAY = "-";

/**
 * Coerces an arbitrary value to a trimmed display string, falling back to `-`.
 *
 * @param value - Raw value from the COB additional-payer payload
 * @returns Non-empty display string, or `-` when missing
 */
const toDisplayString = (value: unknown): string => {
  if (value === undefined || value === null) {
    return EMPTY_DISPLAY;
  }
  const trimmed = String(value).trim();
  return trimmed === "" ? EMPTY_DISPLAY : trimmed;
};

/**
 * Normalizes the payer `active` flag to a display string.
 *
 * @param value - Raw `active` value (`true`/`false`/`null`/missing)
 * @returns `"true"` / `"false"`, or `-` when the source leaves it null/missing
 */
const toActiveDisplay = (value: unknown): string => {
  if (typeof value === "boolean") {
    return String(value);
  }
  return EMPTY_DISPLAY;
};

/**
 * Normalizes the payer effective date to `MM/DD/YYYY` display format.
 *
 * @param value - Raw `effective_date` value in any parseable date format
 * @returns Formatted date, or `-` when missing or unparseable
 */
const toEffectiveDateDisplay = (value: unknown): string => {
  const raw = toDisplayString(value);
  if (raw === EMPTY_DISPLAY) {
    return raw;
  }
  return formatDateToUTCWithFormat(raw, "MM/DD/YYYY", EMPTY_DISPLAY);
};

/**
 * Maps one raw `additional_payers` entry into its display row.
 *
 * `name` is required by the contract but still guarded so a malformed row
 * renders as `-` instead of throwing.
 *
 * @param entry - Raw COB additional-payer record
 * @returns Display row for the "Additional Payers" tab
 */
const toAdditionalPayer = (entry?: CobAdditionalPayer): AdditionalPayer => {
  const row = (entry ?? {}) as Partial<CobAdditionalPayer>;
  return {
    name: toDisplayString(row.name),
    role: toDisplayString(row.role),
    type: toDisplayString(row.type),
    memberId: toDisplayString(row.member_id),
    plan: toDisplayString(row.plan),
    effectiveDate: toEffectiveDateDisplay(row.effective_date),
    active: toActiveDisplay(row.active),
  };
};

/**
 * Whether a value is a usable COB metadata block.
 *
 * The RPA postprocessor always writes `additional_payers` as an array
 * (possibly empty); requiring it distinguishes a real COB report from a
 * missing or malformed `cob` payload.
 *
 * @param value - Raw `processing_metadata.cob` value
 * @returns `true` when the value matches the COB metadata shape
 */
const isCobMetadata = (
  value: unknown,
): value is CobMetadata & { additional_payers: CobAdditionalPayer[] } =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Array.isArray((value as Record<string, unknown>).additional_payers);

/**
 * Builds the read-only "Additional Payers" display model from a tier's COB
 * report.
 *
 * When `processing_metadata.cob` is absent or malformed, or when
 * `additional_payers` is empty, the result is `undefined` so the UI hides
 * the tab entirely (legacy orders without COB data — or with no additional
 * payers found — keep the existing screen).
 *
 * `oncoemr_mismatch` is intentionally ignored — its display is on hold.
 *
 * @param tierCoverageResult - The `coverage_result` row for this tier
 * @returns COB display model, or `undefined` when no usable COB data exists
 */
export const buildAdditionalPayerCoverageInformation = (
  tierCoverageResult?: CoverageResult,
): AdditionalPayerInformation | undefined => {
  const processingMetadata = tierCoverageResult?.processing_metadata as
    Record<string, unknown> | undefined;
  const cob = processingMetadata?.cob;
  if (!isCobMetadata(cob)) {
    return undefined;
  }
  const payers = cob.additional_payers.map((entry) => toAdditionalPayer(entry));
  if (payers.length === 0) {
    return undefined;
  }
  return {
    hasOtherCoverage: cob.has_other_coverage === true,
    payers,
  };
};
